import type { FC, ReactNode } from 'react'

import { useEffect, useRef, useState } from 'react'
import { Check, Share2 } from 'lucide-react'
import { cn } from '@/lib/utils'
import { appLinks, siteConfig } from '@/constants/site'

const copyWithFallback = (text: string): boolean => {
    const field = document.createElement('textarea')

    field.value = text
    field.setAttribute('readonly', '')
    field.style.position = 'fixed'
    field.style.opacity = '0'
    document.body.appendChild(field)
    field.select()

    let copied = false

    try {
        copied = document.execCommand('copy')
    }
    catch {
        copied = false
    }

    document.body.removeChild(field)

    return copied
}

interface ShareButtonProps {
    className?: string
}

// Opens the device share sheet when there is one, otherwise copies the Google Play link.
const ShareButton: FC<ShareButtonProps> = ({ className }): ReactNode => {
    const [copied, setCopied] = useState(false)
    const timer = useRef<number | undefined>(undefined)

    useEffect(() => () => window.clearTimeout(timer.current), [])

    const showCopied = () => {
        setCopied(true)
        window.clearTimeout(timer.current)
        timer.current = window.setTimeout(() => setCopied(false), 2400)
    }

    const handleClick = async () => {
        const url = appLinks.playStore

        if (typeof navigator.share === 'function') {
            try {
                await navigator.share({ title: siteConfig.name, text: siteConfig.tagline, url })
                return
            }
            catch (error) {
                if (error instanceof DOMException && error.name === 'AbortError') return
            }
        }

        try {
            await navigator.clipboard.writeText(url)
            showCopied()
            return
        }
        catch {
            if (copyWithFallback(url)) {
                showCopied()
                return
            }
        }

        window.open(url, '_blank', 'noopener,noreferrer')
    }

    return (
        <>
            <button
                type='button'
                onClick={handleClick}
                className={cn(
                    'group inline-flex h-13 items-center justify-center gap-3 rounded-xl border border-border bg-card px-6 text-base font-semibold text-foreground transition-colors hover:border-foreground/25 hover:bg-muted',
                    className
                )}
            >
                {copied ? 'Link copied' : 'Share the app'}

                {copied
                    ? <Check className='size-4 text-emerald-500' strokeWidth={2.5} />
                    : <Share2 className='size-4' />}
            </button>

            <span className='sr-only' aria-live='polite'>
                {copied ? 'The Google Play link was copied to your clipboard.' : ''}
            </span>
        </>
    )
}

export default ShareButton
